import { Component, OnInit, NgZone } from "@angular/core";
import { QuizService } from "../../service/quiz.service";
import { TaskService } from "src/app/service/tasks.service";
import { Router, ActivatedRoute } from "@angular/router";
import { FormBuilder } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { LoadingDialog } from "../dialogs/loading/loading.dialog";
import { TranslateService } from "@ngx-translate/core";
import { BlockDialog } from "../dialogs/block/block.dialog";
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from "@angular/cdk/drag-drop";
import { Observable } from "rxjs";

type Question = {
  text: string;
  num: number;
  type: string;
  availableOptions?: Option[];
  selectedOptions?: Option[];
  freeFormAnswer?: FreeFormAnswer;
  nextQuestionNum?: number;
  prevQuestionNum?: number;
  hasAnswer?: boolean;
};

type Option = {
  // i added "num" field just in case they'll change their mind
  // and want to save only the number of the answer in the database
  // and not the whole text of options (because saving the plain text of each option is inefficient!)
  // NOTE: it starts from 1 and not 0!
  num: number;
  text: string;
  nextQuestionNum: number;
};

type FreeFormAnswer = {
  label: string;
  text?: string;
  nextQuestionNum: number;
};

@Component({
  selector: "app-questionnaire",
  templateUrl: "./questionnaire.component.html",
  styleUrls: ["./questionnaire.component.css"],
})
export class QuestionnaireComponent implements OnInit {
  // Survey info
  type: string; //query parameter type
  surveyTitle: Observable<string>;
  surveySubTitle: string;

  //Questions
  questions: Question[] = [];
  displayedQuestion: Question;
  emptyText: string = "";

  index: number;

  // Buttons
  disableNext: boolean = true;
  disablePrev: boolean = true;

  // used to display only the necessary element
  isDisplayed: boolean = true;

  isDisabled = true;
  loading = true;
  testCompleted = false;
  dialogRef;

  maxQuestionNumber: number;
  noChange = false;

  constructor(
    public lc: NgZone,
    private quizService: QuizService,
    private taskService: TaskService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.type = this.route.snapshot.queryParamMap.get("type");
    this.setSurveyInfo();

    this.quizService.get_questions(this.type).then((data: any) => {
      // console.log("Questions from database");
      // console.table(datq.availableOptionsbody);
      data.body.forEach((q) => {
        let question: Question = {
          num: q.questionNum,
          text: q.question,
          type: q.answerType,
        };
        switch (q.answerType) {
          case "input":
            var freeFormAnswer: FreeFormAnswer = {
              label: "Type here",
              nextQuestionNum: q.options[0].nextQuestionNum,
            };
            question.freeFormAnswer = freeFormAnswer;
            break;
          case "reasoning":
            var freeFormAnswer: FreeFormAnswer = {
              label: q.options[1].value,
              nextQuestionNum: q.options[1].nextQuestionNum,
            };
            question.freeFormAnswer = freeFormAnswer;
            let option: Option = {
              num: 1,
              text: q.options[0].value,
              nextQuestionNum: q.options[0].nextQuestionNum,
            };
            var availableOptions: Option[] = [];
            availableOptions.push(option);
            question.availableOptions = availableOptions;
            break;
          default:
            var availableOptions: Option[] = [];
            let ansNum = 1;
            q.options.map((o) => {
              let option: Option = {
                num: ansNum++,
                text: o.value,
                nextQuestionNum: o.nextQuestionNum,
              };
              availableOptions.push(option);
            });
            question.availableOptions = availableOptions;
        }
        this.questions.push(question);
      });

      // console.log("Questions");
      // console.table(this.questions)
      console.log(this.questions, null, 2);

      this.index = 1;
      this.setMaxQuestionNumber();
      this.setDisplayedQuestion(1);
      this.checkCompleted();
      this.loading = false;
    });
  }

  // set survey Title and SubTitle:
  setSurveyInfo() {
    let survey = this.type.slice(0, 3).toUpperCase(); // survey will be equal to "COV" or "DEM" or "QUA" or "FAT"
    this.includeTranslations(survey + "_T", survey + "_SUBT");
  }

  includeTranslations(title, subTitle) {
    this.translate.get([title, subTitle]).subscribe((translations) => {
      this.surveyTitle = translations[title];
      this.surveySubTitle = translations[subTitle];
    });
  }

  // ********* GENERAL *********
  private setMaxQuestionNumber() {
    this.maxQuestionNumber = Math.max(...this.questions.map((q) => q.num));
  }

  setDisplayedQuestion(currentQuestionNum: number) {
    this.displayedQuestion = this.questions[currentQuestionNum - 1];
    this.displayedQuestion.prevQuestionNum
      ? (this.disablePrev = false)
      : (this.disablePrev = true);
    this.displayedQuestion.selectedOptions
      ? (this.disableNext = false)
      : (this.disableNext = true);
    this.displayedQuestion.type === "reasoning" &&
    this.displayedQuestion.freeFormAnswer.text
      ? (this.disableNext = false)
      : "";
    this.displayedQuestion.type === "input" &&
    this.displayedQuestion.freeFormAnswer.text
      ? (this.disableNext = false)
      : "";
    if (this.displayedQuestion.type === "drag-and-drop") {
      if (!this.displayedQuestion.selectedOptions) {
        this.displayedQuestion.selectedOptions = [];
        this.disableNext = true;
      } else {
        if (this.displayedQuestion.selectedOptions.length === 0) {
          this.disableNext = true;
        }
      }
    }
    if (this.displayedQuestion.nextQuestionNum > this.maxQuestionNumber) {
      this.disableNext = true;
    }
  }

  private removeSubsequentAnswers(index: number) {
    this.questions.forEach((q) => {
      if (q.num >= index) {
        if (q.type !== "input") {
          delete this.questions[q.num - 1].selectedOptions;
        }
        delete this.questions[q.num - 1].hasAnswer;
        delete this.questions[q.num - 1].nextQuestionNum;
        delete this.questions[q.num - 1].prevQuestionNum;
        if (q.type === "reasoning" || q.type === "input") {
          delete this.questions[q.num - 1].freeFormAnswer.text;
        }
      }
    });
  }

  private setNextAndPrevQuestionNums(currentQuestionNum: number) {
    let nextQuestionNum: number;
    switch (this.displayedQuestion.type) {
      case "reasoning":
      //TO DO: separate the 2 cases
      case "input":
        nextQuestionNum = this.displayedQuestion.freeFormAnswer.nextQuestionNum;
        break;
      default:
        nextQuestionNum = Math.min(
          ...this.displayedQuestion.selectedOptions.map(
            (option) => option.nextQuestionNum
          )
        );
    }
    if (this.questions[currentQuestionNum - 1].nextQuestionNum) {
      if (
        this.questions[currentQuestionNum - 1].nextQuestionNum !==
        nextQuestionNum
      ) {
        this.removeSubsequentAnswers(
          this.questions[currentQuestionNum - 1].nextQuestionNum
        );
      }
    }
    this.questions[currentQuestionNum - 1].nextQuestionNum = nextQuestionNum;
    if (nextQuestionNum > this.maxQuestionNumber) {
      this.disableNext = true;
    } else {
      this.questions[nextQuestionNum - 1].prevQuestionNum = currentQuestionNum;
    }
  }

  showNextQuestion() {
    this.setDisplayedQuestion(this.displayedQuestion.nextQuestionNum);
    this.index++;
  }

  showPrevQuestion() {
    this.setDisplayedQuestion(this.displayedQuestion.prevQuestionNum);
    this.index--;
  }

  isChecked(option: Option) {
    return this.displayedQuestion.selectedOptions
      ?.map((option) => option.num)
      .includes(option.num);
  }

  private isCompleted() {
    let index: number = 0;
    let result: boolean = true;
    while (index < this.maxQuestionNumber) {
      result = result && this.questions[index]?.hasAnswer;
      // console.log("index: " + index + ", result: " + result + " , hasAnswer: " + this.questions[index].hasAnswer)
      index = this.questions[index].nextQuestionNum - 1;
    }
    return result;
  }

  checkCompleted() {
    this.isCompleted()
      ? (this.testCompleted = true)
      : (this.testCompleted = false);
  }
  // ********* END GENERAL *********

  // ********* SINGLE SELECT *********
  private addSingleOption(option: Option, currentQuestionNum) {
    this.questions[currentQuestionNum - 1].selectedOptions = [option];
    this.questions[currentQuestionNum - 1].hasAnswer = true;
  }

  onSingleSelect(option: Option) {
    if (this.displayedQuestion.type === "reasoning") {
      delete this.questions[this.displayedQuestion.num - 1].freeFormAnswer.text;
    }
    this.addSingleOption(option, this.displayedQuestion.num);
    if (this.displayedQuestion.selectedOptions) {
      this.disableNext = false;
      this.setNextAndPrevQuestionNums(this.displayedQuestion.num);
    }
    // console.log("questions after adding a single option");
    // console.table(this.questions); //ok
    this.checkCompleted();
  }

  onSingleSelectSpecial(option: Option) {
    let hadCovid = true;
    this.addSingleOption(option, this.displayedQuestion.num);
    this.setNextAndPrevQuestionNums(this.displayedQuestion.num);
    if (option.num === 3) {
      hadCovid = false;
      this.disableNext = true;
      this.dialogRef = this.dialog.open(BlockDialog, { disableClose: true });
      this.dialogRef.afterClosed().subscribe(() => {
        // console.log("sono in subscribe");
        // console.log("displayed question");
        // console.log(this.displayedQuestion);
        delete this.questions[0].selectedOptions;
      });
    } else {
      this.disableNext = false;
    }
    console.log("questions after adding a single option SPECIAL");
    console.table(this.questions); //ok
    this.checkCompleted();
  }
  // ********* END SINGLE SELECT *********

  // ********* MULTI SELECT SPECIAL *********
  private alreadyAddedMulti(option: Option) {
    return this.questions[
      this.displayedQuestion.num - 1
    ].selectedOptions.includes(option);
  }
  private addOrRemoveMultiOption(option: Option) {
    if (this.questions[this.displayedQuestion.num - 1].selectedOptions) {
      if (this.alreadyAddedMulti(option)) {
        // console.log("ALREADY ADDED, SO I'M REMOVING IT")
        // console.log("options before filter")
        // console.table(this.questions[this.displayedQuestion.num - 1].selectedOptions)
        this.questions[this.displayedQuestion.num - 1].selectedOptions =
          this.questions[this.displayedQuestion.num - 1].selectedOptions.filter(
            (o) => o !== option
          );
        // console.log("options after filter")
        // console.table(this.questions[this.displayedQuestion.num - 1].selectedOptions)
        if (
          this.questions[this.displayedQuestion.num - 1].selectedOptions
            .length === 0
        ) {
          delete this.questions[this.displayedQuestion.num - 1].selectedOptions;
        }
      } else {
        // console.log("adding a new option")
        if (option.text === "None of the above" || option.text === "None") {
          this.questions[this.displayedQuestion.num - 1].selectedOptions = [
            option,
          ];
        } else {
          this.questions[this.displayedQuestion.num - 1].selectedOptions =
            this.questions[
              this.displayedQuestion.num - 1
            ].selectedOptions.filter(
              (o) => o.text !== "None of the above" && o.text !== "None"
            );

          this.questions[this.displayedQuestion.num - 1].selectedOptions.push(
            option
          );
        }
        // console.log("all the options added")
        // console.table(this.questions[this.displayedQuestion.num - 1].selectedOptions)
      }
    } else {
      this.questions[this.displayedQuestion.num - 1].selectedOptions = [option];
    }
  }

  onMultiSelect(option: Option) {
    // console.log("I just selected this option: ");
    // console.log(option) // ok
    this.addOrRemoveMultiOption(option);
    if (this.displayedQuestion.selectedOptions) {
      this.disableNext = false;
      this.setNextAndPrevQuestionNums(this.displayedQuestion.num);
    } else {
      this.disableNext = true;
    }
    if (this.questions[this.displayedQuestion.num - 1].selectedOptions) {
      this.questions[this.displayedQuestion.num - 1].hasAnswer = true;
    } else {
      delete this.questions[this.displayedQuestion.num - 1].hasAnswer;
    }
    console.log("questions after adding a multi select option");
    console.table(this.questions); //ok
    this.checkCompleted();
  }
  // ********* END MULTI SELECT SPECIAL *********

  // ********* REASONING *********
  remainingChars = 510;
  onType(event: any) {
    if (
      this.displayedQuestion.type === "reasoning" ||
      this.displayedQuestion.type === "input"
    ) {
      this.remainingChars = 510 - event.target.value.length;
    }
    if (this.displayedQuestion.type === "reasoning") {
      delete this.questions[this.displayedQuestion.num - 1].selectedOptions;
    }
    const textArea = document.getElementById(
      this.displayedQuestion.num.toString()
    ) as HTMLTextAreaElement;
    // console.log(textArea.value)
    this.questions[this.displayedQuestion.num - 1].freeFormAnswer.text =
      textArea.value;
    if (textArea.value.length > 0) {
      this.questions[this.displayedQuestion.num - 1].hasAnswer = true;
      this.disableNext = false;
      this.setNextAndPrevQuestionNums(this.displayedQuestion.num);
    } else {
      delete this.questions[this.displayedQuestion.num - 1].hasAnswer;
      delete this.questions[this.displayedQuestion.num - 1].nextQuestionNum;
      if (this.displayedQuestion.num < this.maxQuestionNumber) {
        delete this.questions[this.displayedQuestion.num].prevQuestionNum;
      }
      this.disableNext = true;
    }
    console.log("Questions after onTypeReasoning");
    console.table(this.questions);
    this.checkCompleted();
  }
  // ********* END REASONING *********

  // ********* DRAG AND DROP *********
  onDrop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      event.container.data.forEach((x) => console.log(x));
      // console.log("previousIndex: " + event.previousIndex);
      // console.log("currentIndex: " + event.currentIndex);
    }
    if (this.questions[this.displayedQuestion.num - 1].selectedOptions) {
      if (
        this.questions[this.displayedQuestion.num - 1].selectedOptions
          .length === 0
      ) {
        delete this.questions[this.displayedQuestion.num - 1].selectedOptions;
        delete this.questions[this.displayedQuestion.num - 1].hasAnswer;
        this.disableNext = true;
        this.displayedQuestion.selectedOptions = [];
        console.table(this.questions);
      } else {
        this.disableNext = false;
        this.setNextAndPrevQuestionNums(this.displayedQuestion.num);
      }
    } else {
      delete this.questions[this.displayedQuestion.num - 1].selectedOptions;
      this.disableNext = true;
    }

    if (
      this.questions[this.displayedQuestion.num - 1].selectedOptions &&
      this.displayedQuestion.selectedOptions.length !== 0
    ) {
      this.questions[this.displayedQuestion.num - 1].hasAnswer = true;
    } else {
      delete this.questions[this.displayedQuestion.num - 1].hasAnswer;
    }

    console.log("questions after drag & drop");
    console.table(this.questions); //ok
    this.checkCompleted();
  }
  // ********* END DRAG AND DROP *********

  onSubmit(): void {
    const postRequest = {
      question_id: [],
      answer: [],
      type: this.type,
    };

    this.questions = this.questions.filter((q) => q.hasAnswer);
    console.log(this.questions, null, 2);
    this.questions.forEach((q) => {
      postRequest.question_id.push(q.num);
      switch (q.type) {
        case "reasoning":
          if (q.selectedOptions) {
            postRequest.answer.push({
              text: q.selectedOptions[0].text.replace(/[“”]/g, '"'),
              id: q.selectedOptions[0].num,
            });
          } else {
            postRequest.answer.push({
              text: q.freeFormAnswer.text.replace(/[“”]/g, '"'),
              id: 0,
            });
          }
          break;
        case "input":
          postRequest.answer.push(q.freeFormAnswer.text);
          break;
        case "single-select":
          postRequest.answer.push({
            text: q.selectedOptions[0].text.replace(/[“”]/g, '"'),
            id: q.selectedOptions[0].num,
          });
          break;
        case "single-select-special":
          postRequest.answer.push({
            text: q.selectedOptions[0].text.replace(/[“”]/g, '"'),
            id: q.selectedOptions[0].num,
          });
          break;
        case "drag-and-drop":
          let mergedArray = [...q.availableOptions, ...q.selectedOptions];

          mergedArray.sort((item1, item2) => item1.num - item2.num);

          let dragId = mergedArray.map((item) => {
            const indexInB = q.selectedOptions.findIndex(
              (bItem) => bItem.num === item.num
            );
            return indexInB !== -1 ? indexInB + 1 : 0;
          });

          // console.log("Merged Array:", mergedArray);

          postRequest.answer.push({
            id: dragId.join(""),
            text: q.selectedOptions.map((o) => o.text).join(", "),
          });

          break;
        case "multi-select":
          let id = [];
          function objectExistsInArray(object, array) {
            return array.some(
              (item) =>
                item.num === object.num &&
                item.text === object.text &&
                item.nextQuestionNum === object.nextQuestionNum
            );
          }

          for (let obj of q.availableOptions) {
            if (objectExistsInArray(obj, q.selectedOptions)) {
              id.push(1);
            } else {
              id.push(0);
            }
          }
          postRequest.answer.push({
            id: id.join(""),
            text: q.selectedOptions.map((o) => o.text).join(", "),
          });
        default:
          postRequest.answer.push(
            q.selectedOptions.map((o) => o.text).join(", ")
          );
      }
    });
    this.dialogRef = this.dialog.open(LoadingDialog, { disableClose: true });
    this.post_answers(postRequest);
    // console.log(postRequest, null, 2);
  }
  post_answers(data) {
    this.quizService
      .post_answers(data)
      .then(() => {
        this.dialogRef.close();
        this.isDisplayed = false;
        setTimeout(() => {
          this.router.navigate([""]);
        }, 3000);
      })
      .then(() => {
        this.taskService.saveCompletedTask({ type: this.type });
      })
      .catch((e) => {
        console.log(e);
        this.router.navigate([""]);
      });
  }
}
